import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminShell } from "@/components/AdminShell";
import { AdminTooltip } from "@/components/admin/AdminTooltip";
import { Button } from "@/components/ui/button";
import { getSearchConsoleReport } from "@/lib/search-console.functions";

export const Route = createFileRoute("/_authenticated/admin/sokord")({
  head: () => ({
    meta: [
      { title: "Sökord från Google — Sellavo admin" },
      {
        name: "description",
        content: "Vilka sökord folk använder för att hitta Sellavo, direkt från Google Search Console.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SokordPage,
});

const RANGES = [
  { days: 7, label: "7 dagar" },
  { days: 28, label: "28 dagar" },
  { days: 90, label: "90 dagar" },
];

function pct(n: number) {
  return `${(n * 100).toFixed(1)} %`;
}

type Row = { key: string; clicks: number; impressions: number; ctr: number; position: number };
type SortKey = "key" | "clicks" | "impressions" | "ctr" | "position";

const COLUMN_HELP: Record<SortKey, string> = {
  key: "Raden det gäller.",
  clicks:
    "Antal gånger någon faktiskt klickade sig in på Sellavo från Googles sökresultat för den här raden.",
  impressions:
    "Antal gånger raden visades i Googles sökresultat – alltså hur ofta folk såg oss, oavsett om de klickade.",
  ctr: "Klickandel = klick delat med visningar. Visar hur lockande titeln och beskrivningen är i sökresultatet.",
  position:
    "Snittplacering i Google. 1–10 = sida ett, 11–20 = sida två. Lägre siffra är bättre.",
};

function Table({
  title,
  help,
  rows,
  keyLabel,
  linkify,
}: {
  title: string;
  help: string;
  keyLabel: string;
  rows: Row[];
  linkify?: "url" | "query";
}) {
  const [sort, setSort] = useState<SortKey>("clicks");
  const [dir, setDir] = useState<"asc" | "desc">("desc");

  const sorted = [...rows].sort((a, b) => {
    const av = a[sort];
    const bv = b[sort];
    const cmp =
      typeof av === "string" && typeof bv === "string"
        ? av.localeCompare(bv, "sv")
        : (av as number) - (bv as number);
    return dir === "asc" ? cmp : -cmp;
  });

  function toggle(k: SortKey) {
    if (k === sort) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(k);
      setDir(k === "key" ? "asc" : "desc");
    }
  }

  const cols: { k: SortKey; label: string }[] = [
    { k: "key", label: keyLabel },
    { k: "clicks", label: "Klick" },
    { k: "impressions", label: "Visningar" },
    { k: "ctr", label: "Klickandel" },
    { k: "position", label: "Snittplacering" },
  ];

  function href(key: string) {
    if (linkify === "url") return key.startsWith("http") ? key : `https://${key}`;
    if (linkify === "query")
      return `https://www.google.com/search?q=${encodeURIComponent(key)}`;
    return null;
  }

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <AdminTooltip text={help}>
        <h2 className="font-heading text-lg font-semibold">{title}</h2>
      </AdminTooltip>
      <p className="mt-1 text-sm text-muted-foreground">{help}</p>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Google har inte rapporterat någon data för perioden än.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {cols.map((c) => (
                  <th key={c.k} className="py-2 pr-3 font-medium">
                    <AdminTooltip text={COLUMN_HELP[c.k]}>
                      <button
                        type="button"
                        onClick={() => toggle(c.k)}
                        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground"
                      >
                        {c.label}
                        <span className="text-[10px]">
                          {sort === c.k ? (dir === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    </AdminTooltip>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, 30).map((r) => {
                const link = href(r.key);
                return (
                  <tr key={r.key} className="border-t border-border hover:bg-muted/40">
                    <td className="max-w-[380px] truncate py-2 pr-3">
                      {link ? (
                        <a
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary underline underline-offset-2"
                        >
                          {r.key}
                        </a>
                      ) : (
                        r.key
                      )}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">{r.clicks}</td>
                    <td className="py-2 pr-3 tabular-nums">{r.impressions}</td>
                    <td className="py-2 pr-3 tabular-nums">{pct(r.ctr)}</td>
                    <td className="py-2 tabular-nums">{r.position.toFixed(1)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SokordPage() {
  const [days, setDays] = useState(28);
  const [site, setSite] = useState<string | undefined>(undefined);
  const fetchReport = useServerFn(getSearchConsoleReport);
  const { data, isLoading } = useQuery({
    queryKey: ["search-console", days, site ?? ""],
    queryFn: () => fetchReport({ data: { days, site } }),
    staleTime: 10 * 60_000,
  });

  return (
    <AdminShell title="Sökord från Google">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-2xl text-sm text-foreground/75">
            Hämtas direkt från Google Search Console. Google släpar ungefär tre dygn, så senaste
            dagarna saknas alltid. Klick = besök från Googles resultat, visningar = hur ofta sidan
            syntes i resultatet.
          </p>
          <div className="flex gap-1.5">
            {RANGES.map((r) => (
              <Button
                key={r.days}
                size="sm"
                variant={days === r.days ? "default" : "outline"}
                onClick={() => setDays(r.days)}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Hämtar data från Google…</p>}

        {data?.status === "error" && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {data.message}
          </p>
        )}

        {data?.status === "no_property" && (
          <div className="rounded-md border border-border bg-card p-4 text-sm text-foreground/80">
            Kontot är kopplat, men ingen verifierad egenskap för sellavo.com hittades i Search
            Console. Lägg till och verifiera sellavo.com i Google Search Console, så dyker sökorden
            upp här.
          </div>
        )}

        {data?.status === "selection_required" && (
          <div className="rounded-md border border-border bg-card p-4 text-sm">
            <p className="font-medium">Flera egenskaper matchar sellavo.com – välj en:</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {data.sites.map((s: string) => (
                <Button key={s} size="sm" variant="outline" onClick={() => setSite(s)}>
                  {s}
                </Button>
              ))}
            </div>
          </div>
        )}

        {data?.status === "ok" && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <AdminTooltip text="Antal gånger någon klickade sig in på Sellavo från Googles sökresultat.">
                <div className="rounded-md border border-border bg-card p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Klick från Google
                  </p>
                  <p className="mt-2 font-heading text-2xl font-bold">
                    {data.totals.clicks.toLocaleString("sv-SE")}
                  </p>
                </div>
              </AdminTooltip>
              <AdminTooltip text="Antal gånger en Sellavo-sida visades i sökresultatet, oavsett om någon klickade.">
                <div className="rounded-md border border-border bg-card p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Visningar i Google
                  </p>
                  <p className="mt-2 font-heading text-2xl font-bold">
                    {data.totals.impressions.toLocaleString("sv-SE")}
                  </p>
                </div>
              </AdminTooltip>
              <AdminTooltip text="Vilken Search Console-egenskap och vilken period siffrorna kommer ifrån.">
                <div className="rounded-md border border-border bg-card p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Period
                  </p>
                  <p className="mt-2 text-sm font-medium">
                    {data.startDate} – {data.endDate}
                  </p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">{data.siteUrl}</p>
                </div>
              </AdminTooltip>
            </div>

            <Table
              title="Sökord"
              keyLabel="Sökord"
              linkify="query"
              help="Orden folk skrev i Google innan de såg eller klickade på Sellavo. Ligger placeringen på 11–20 är sidan på sida två – då räcker det ofta med lite bättre text för att nå sida ett."
              rows={data.queries}
            />
            <Table
              title="Sidor"
              keyLabel="Adress"
              linkify="url"
              help="Vilka sidor på Sellavo som faktiskt får trafik från Google. Bra facit på om guiderna och kategorisidorna fungerar."
              rows={data.pages}
            />
            <Table
              title="Länder"
              keyLabel="Land"
              help="Var i världen sökningarna görs. Nästan allt bör vara Sverige."
              rows={data.countries}
            />
            <Table
              title="Enheter"
              keyLabel="Enhet"
              help="Mobil, dator eller surfplatta – visar vilken vy som är viktigast att optimera."
              rows={data.devices}
            />
          </>
        )}
      </div>
    </AdminShell>
  );
}
